// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { TagPresentationProps } from "@insite/mobius/Tag";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, hideElement, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

interface MyAccountFilterSectionStyles {
    filterContainer?: GridContainerProps;
    countGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    searchFormStyles: {
        wrapper?: InjectableCss;
        headingText?: TypographyPresentationProps;
    };
    clearButton?: ButtonPresentationProps;
    datesGridItem?: GridItemProps;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    smallGridItem?: GridItemProps;
    mediumGridItem?: GridItemProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFilterTag?: TagPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    searchFieldWrapper?: InjectableCss;
}

const myAccountFilterSectionStyles: MyAccountFilterSectionStyles = {
    filterContainer: {
        gap: 20,
        css: css`
            padding: 20px 13px 13px 13px;
            ${xScreen("lte", "md")} {
                padding: 0;
            }
        `,
    },
    clearButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    searchFormStyles: {
        wrapper: {
            css: css`
                background: ${color("common.white")};
                border: 1px solid ${color("common.border")};
                padding: 30px 30px 15px;
                position: relative;
                &:before {
                    content: " ";
                    width: 118px;
                    height: 3px;
                    position: absolute;
                    top: -2px;
                    background: white;
                    left: 0;
                }

                ${GridItemStyle} {
                    align-items: end;
                }
            `,
        },
        headingText: {
            css: css`
                ${hideElement}
            `,
        },
    },
    orderTotalGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    mediumGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 0;
            `,
        },
        label: translate("Order Total Amount"),
    } as TextFieldProps,
    datesGridItem: {
        width: [12, 12, 12, 4, 4],
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    smallGridItem: {
        width: [12, 6, 3, 3, 2],
    },
    datePickersWrapper: {
        css: css`
            display: flex;
            > div {
                width: 50%;
            }
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                > div {
                    width: auto;
                }
            }
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            datePicker: css`
                ${xScreen("lte", "md")} {
                    .react-calendar {
                        left: unset;
                        right: 0;
                    }
                }
            `,
        },
    },
    appliedFiltersContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        `,
    },
    appliedFilterTag: {
        css: css`
            margin: 0;
        `,
    },
    searchFieldWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex: 0 1 calc(100% / 6 - 12px);
            min-width: 130px;

            ${xScreen("lte", "md")} {
                flex: 1;
                min-width: 100%;
                max-width: 100%;
            }
        `,
    },
};

export default myAccountFilterSectionStyles;
